/* eslint-disable @typescript-eslint/no-unused-expressions */
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import { ISliderData } from 'components/FilterViewAdvanceV2/filterTyes';
import { AllocationStatus } from 'constants/constant';
import { State } from 'features/CampaignPlanning/Tab';
import { PayloadSaveDraft } from 'features/CampaignPlanning/types';
import { PostOutreachSaveType } from 'features/StrategyConfig/CreatePlan/CreatePlanSaveWrapper';
import {
  NewCampaignResponseData,
  SegmentDateMappings,
} from 'features/StrategyConfig/CreatePlan/Flow/strategyTestData';
import {
  ConditionOutreachType,
  ConditionType,
  Data,
  OutreachType,
} from 'features/StrategyConfig/CreatePlan/Flow/types';
import { IActionPayload } from 'redux/types/Outreach.types';
import { convertISOToLocaleDateString, getDatesBetween } from 'utils/date';

import '../../../styles/index.css';
import {
  ICampaignData,
  NbaCampaignStatus,
} from '../campaign-planner/CampaignDetails.types';
import {
  CreateCampaignActionType,
  UpdateCampaignActionType,
} from '../campaign-planner/types';
import {
  checkPlansConflictWithStrategy,
  checkStrategyConflictWithStrategyAndPlan,
  dateDifferenceInDays,
  getFieldsForFirstOutreachFirstDay,
  increaseDateByOneDay,
} from './helper';
import {
  CreateCampaignWithAllocationResponseData,
  SegmentsListType,
  StrategyListType,
} from './strategy.saga';
import { SegmentPayload } from './types';

export type DateList = {
  date: string;
  state: State;
  dayCount: number;
};

export type ChannelDropdown = {
  displayName: string;
  productName: string;
};
export type Channel = 'Ivr' | 'Sms' | 'Whatsapp';
export interface Content {
  id: number;
  name: string;
  message: string;
}
export interface OutreachConfig {
  channel: ChannelDropdown[];
  languages: {
    [key in Channel]: string[];
  };
  contents: Content[];
}

export type ActiveStrategySwitch = {
  segmentId: number;
  enableStrategyLibrary: boolean;
};

export enum CampaignExecutionStatus {
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  EXECUTION_DUE = 'execution-due',
  NONE = '',
}
export type StrategyStateInitialStateTypes = {
  isLoading: boolean;
  isCampaignPlanningLoading: boolean;
  strategyData: Map<string, Array<ConditionOutreachType>>;
  selectedPlanDay: number;
  createStrategy: Data;
  conditionsDropdownList: ConditionType[];
  outreachesDropdownList: OutreachType[];
  isStrategyCreated: boolean;
  strategiesList: StrategyListType[];
  totalRecords: number;
  campaignPlanSelectedDay: DateList;
  campaignPlanner: NewCampaignResponseData;
  campaignDates: Record<string, string>;
  segments: { segmentLogic: string; segmentName: string }[];
  campaignEndStartDates: {
    startDate: string;
    endDate: string;
    daysCount: number;
  };
  dateList: DateList[];
  activeAccordions: string[];
  outreachConfig: OutreachConfig;
  activeStrategySwitch: ActiveStrategySwitch[];
  tos: string;
  selectedChannel: string;
};
export type DeleteConditionType = {
  deleteConditions: string[];
  selectedPlanDay: number;
};

export type AddConditionOutreachType = {
  conditionOutreaches: ConditionOutreachType;
  selectedPlanDay: number;
  selectedOutreachDayCount: number;
  accumulatedTimeSlotsInMinutes: number;
};
export type DeleteOutreachType = {
  nodeId: string;
  outreachId: number;
  selectedPlanDay: number;
};

export type AddOutreachType = {
  currentNode: string;
  selectedOutreaches: OutreachType[];
  selectedPlanDay: number;
};
export const strategyInitialState: StrategyStateInitialStateTypes = {
  isLoading: false,
  strategyData: new Map<string, Array<ConditionOutreachType>>(),
  selectedPlanDay: 1,
  createStrategy: {
    strategyName: '',
    daysCount: 0,
    intensity: '',
    isMapped: false,
    isLibrary: true,
    createdDate: '',
  },
  conditionsDropdownList: [],
  outreachesDropdownList: [],
  isStrategyCreated: false,
  strategiesList: [],
  campaignPlanSelectedDay: {
    date: '',
    state: State.FUTURE,
    dayCount: 0,
  },
  campaignPlanner: {
    campaignName: '',
    startDate: '',
    allocationId: '',
    endDate: '',
    allocationName: '',
    currentStatus: AllocationStatus.NONE,
    createdAt: '',
    campaignId: 0,
    campaignSegmentList: [],
    records: '',
    risks: '',
    lenderId: '',
    clientId: '',
    tos: '',
    paused: false,
    campaignExecutionStatus: CampaignExecutionStatus.NONE,
    currentDateAndTime: -1,
    edited: false,
    isNba: undefined,
    campaignNbaStatus: {
      nbaStatus: NbaCampaignStatus.WAITING,
      ticketRaised: false,
    },
    lob: '',
    allocationStartDate: '',
    allocationEndDate: '',
  },
  activeAccordions: [],
  campaignEndStartDates: {
    startDate: '',
    endDate: '',
    daysCount: 0,
  },
  dateList: [],
  segments: [],
  campaignDates: {},
  totalRecords: 0,
  outreachConfig: {
    channel: [],
    languages: {
      Ivr: [],
      Sms: [],
      Whatsapp: [],
    },
    contents: [],
  },
  activeStrategySwitch: [],
  tos: '',
  selectedChannel: '',
  isCampaignPlanningLoading: false,
};

const strategy = createSlice({
  name: 'strategy',
  initialState: strategyInitialState,
  reducers: {
    addNewOutreach: (
      state,
      action: PayloadAction<{
        planIndex: number;
        outreachPayload: AddOutreachType;
      }>
    ) => {
      state.createStrategy.days?.forEach((value) => {
        if (
          value.currentDay === action.payload.outreachPayload.selectedPlanDay
        ) {
          value.plans.forEach((plan, index) => {
            if (index === action.payload.planIndex) {
              plan.planConditionoutreachList.some((condition) => {
                if (
                  condition.conditionOutreach.currentNode ===
                  action.payload.outreachPayload.currentNode
                ) {
                  condition.conditionOutreach.outreach.push(
                    ...action.payload.outreachPayload.selectedOutreaches
                  );
                  return true;
                }
                return false;
              });
            }
          });
        }
      });
    },
    addConditionOutreachAction: (
      state,
      action: PayloadAction<{
        planIndex: number;
        payload: AddConditionOutreachType;
      }>
    ) => {
      state.createStrategy.days?.forEach((value) => {
        if (value.currentDay === action.payload.payload.selectedPlanDay) {
          value.plans.forEach((plan, index) => {
            if (index === action.payload.planIndex) {
              plan.planConditionoutreachList.push(
                action.payload.payload.conditionOutreaches
              );
            }
          });
        }
      });
    },
    selectPlanDays: (state, action: PayloadAction<{ daysCount: number }>) => {
      state.createStrategy.daysCount = action.payload.daysCount;
    },
    selectPlanIntensity: (
      state,
      action: PayloadAction<{ intensity: string }>
    ) => {
      state.createStrategy.intensity = action.payload.intensity;
    },
    selectPlanDay: (
      state,
      action: PayloadAction<{ selectedPlanDay: number }>
    ) => {
      state.selectedPlanDay = action.payload.selectedPlanDay;
    },
    createFirstOutreachPlan: (
      state,
      action: PayloadAction<PostOutreachSaveType>
    ) => {
      const selectedDay = state.createStrategy.days?.find((value) => {
        if (value.currentDay === action.payload.current_day) {
          return true;
        }
        return false;
      });

      if (selectedDay) {
        selectedDay.plans.push(
          getFieldsForFirstOutreachFirstDay(action.payload).plans[0]
        );
        return;
      }

      state.createStrategy.days?.push(
        getFieldsForFirstOutreachFirstDay(action.payload)
      );
    },
    fetchStrategyById: (
      state,
      action: PayloadAction<{ strategyId: string }>
    ) => {
      state.isLoading = true;
    },
    fetchStrategyByIdSuccess: (
      state,
      action: PayloadAction<{ strategy: Data; isMapped: boolean }>
    ) => {
      state.isLoading = false;
      state.createStrategy = action.payload.strategy;
      state.createStrategy.isMapped = action.payload.isMapped;
    },
    setLoadingStrategyState: (
      state,
      action: PayloadAction<{ isLoading: boolean }>
    ) => {
      state.isLoading = action.payload.isLoading;
    },
    fetchConditionsDropdownList: (state) => {
      state.isLoading = true;
    },
    fetchConditionsDropdownListSuccess: (
      state,
      action: PayloadAction<{ conditions: ConditionType[] }>
    ) => {
      state.isLoading = false;
      state.conditionsDropdownList = action.payload.conditions;
    },
    fetchOutreachDropdownList: (state) => {
      state.isLoading = true;
    },
    fetchOutreachDropdownListSuccess: (
      state,
      action: PayloadAction<{ outreaches: OutreachType[] }>
    ) => {
      state.isLoading = false;
      state.outreachesDropdownList = action.payload.outreaches;
    },
    deleteConditionOutreaches: (
      state,
      action: PayloadAction<{
        planIndex: number;
        conditionPayload: DeleteConditionType;
      }>
    ) => {
      state.createStrategy.days?.forEach((value) => {
        if (
          value.currentDay === action.payload.conditionPayload.selectedPlanDay
        ) {
          value.plans.forEach((plan, index) => {
            if (index === action.payload.planIndex) {
              plan.planConditionoutreachList =
                plan.planConditionoutreachList.filter((condition) => {
                  if (
                    action.payload.conditionPayload.deleteConditions.includes(
                      condition.conditionOutreach.currentNode
                    )
                  ) {
                    return false;
                  }
                  return true;
                });
            }
          });
        }
      });
    },
    deleteOutreach: (
      state,
      action: PayloadAction<{
        planIndex: number;
        outReachPayload: DeleteOutreachType;
      }>
    ) => {
      state.createStrategy.days?.forEach((value) => {
        if (
          value.currentDay === action.payload.outReachPayload.selectedPlanDay
        ) {
          value.plans.forEach((plan, index) => {
            if (index === action.payload.planIndex) {
              plan.planConditionoutreachList.some((condition) => {
                if (
                  condition.conditionOutreach.currentNode ===
                  action.payload.outReachPayload.nodeId
                ) {
                  condition.conditionOutreach.outreach =
                    condition.conditionOutreach.outreach.filter((outreach) => {
                      if (
                        outreach.outreachId ===
                        action.payload.outReachPayload.outreachId
                      ) {
                        return false;
                      }
                      return true;
                    });
                  return true;
                }
                return false;
              });
            }
          });
        }
      });
    },
    fetchStrategies: (
      state,
      action: PayloadAction<
        | {
            offset: number;
            limit: number;
            appliedFilterState: Record<string, string[] | ISliderData>;
            searchText: string;
          }
        | undefined
      >
    ) => {
      state.isLoading = true;
    },
    fetchStrategiesSuccess: (
      state,
      action: PayloadAction<{
        strategiesList: StrategyListType[];
        totalRecords: number;
      }>
    ) => {
      state.isLoading = false;
      state.strategiesList = action.payload.strategiesList;
      state.totalRecords = action.payload.totalRecords;
    },
    fetchStrategiesFail: (state) => {
      state.isLoading = false;
    },
    resetStrategiesList: (state) => {
      state.strategiesList = [];
    },
    selectCampaignPlanDate: (
      state,
      action: PayloadAction<{ campaignPlanSelectedDay: DateList }>
    ) => {
      state.campaignPlanSelectedDay = action.payload.campaignPlanSelectedDay;
    },
    fetchCampaignPlan: (
      state,
      action: PayloadAction<{
        allocationId: number;
      }>
    ) => {
      state.isLoading = true;
    },
    fetchCampaignPlanSuccess: (
      state,
      action: PayloadAction<{
        campaignPlanner: NewCampaignResponseData;
        campaignDates: Record<string, string>;
      }>
    ) => {
      state.campaignPlanner = action.payload.campaignPlanner;
      state.campaignPlanner.campaignName =
        action.payload.campaignPlanner.campaignName;
      state.campaignEndStartDates.endDate =
        action.payload.campaignPlanner.endDate;
      state.campaignEndStartDates.startDate =
        action.payload.campaignPlanner.startDate;
      state.campaignPlanner = action.payload.campaignPlanner;
      state.campaignPlanner.edited = false;
      state.campaignPlanner.isNba = action.payload.campaignPlanner.isNba;
      const dateList = getDatesBetween(
        new Date(action.payload.campaignPlanner.startDate),
        new Date(action.payload.campaignPlanner.endDate)
      );

      state.dateList = dateList;
      state.campaignPlanSelectedDay = dateList[0];
      state.campaignEndStartDates.daysCount =
        dateDifferenceInDays(
          action.payload.campaignPlanner.startDate,
          action.payload.campaignPlanner.endDate
        ) + 1;
      state.campaignDates = action.payload.campaignDates;
      action.payload.campaignPlanner.campaignSegmentList.forEach(
        (campaignSegment) => {
          const newMappings: SegmentDateMappings[] = [];
          campaignSegment.segmentDateMappings.forEach((segmentDateMapping) => {
            segmentDateMapping.strategy?.daysCount;
            const diffInDays = segmentDateMapping.strategy?.daysCount || 1;
            if (segmentDateMapping.strategy?.strategyId && diffInDays > 1) {
              for (let i = 1; i < diffInDays; i++) {
                const newStrategy = {
                  strategyId: segmentDateMapping.strategy?.strategyId,
                  strategyName: segmentDateMapping.strategy?.strategyName,
                  daysCount: diffInDays,
                  totalNoOfOutreaches:
                    segmentDateMapping.strategy?.totalNoOfOutreaches,
                };
                const newSegmentStrategyMapping2: SegmentDateMappings = {
                  ...segmentDateMapping,
                  campaignDate: {
                    campaignDateId: 1,
                    currentDate: increaseDateByOneDay(
                      segmentDateMapping.campaignDate.currentDate,
                      i
                    ),
                  },
                  strategy: newStrategy,
                  plans: [],
                  isBlockedByStrategy: true,
                  blockedByStrategy: {
                    strategyId: segmentDateMapping.strategy?.strategyId,
                    strategyName: segmentDateMapping.strategy?.strategyName,
                    strategyDaysCount: segmentDateMapping.strategy?.daysCount,
                    blockedFrom: segmentDateMapping.campaignDate.currentDate,
                  },
                };
                newMappings.push(newSegmentStrategyMapping2);
              }
            }
          });
          campaignSegment.segmentDateMappings.push(...newMappings);
        }
      );

      state.isLoading = false;
      state.isCampaignPlanningLoading = false;
    },
    createParentOutreachPlanSegment: (
      state,
      action: PayloadAction<{
        payload: PostOutreachSaveType;
        segmentId: number;
        campaignPlanSelectedDay: string;
      }>
    ) => {
      state.campaignPlanner.campaignSegmentList.forEach((campaignSegment) => {
        if (campaignSegment.segment.segmentId === action.payload.segmentId) {
          state.campaignPlanner.campaignSegmentList.forEach(
            (campaignSegment) => {
              if (
                campaignSegment.segment.segmentId === action.payload.segmentId
              ) {
                const selectedSegmentDateMappings =
                  campaignSegment.segmentDateMappings.find(
                    (segmentDateMapping) => {
                      if (
                        segmentDateMapping.campaignDate.currentDate ===
                        action.payload.campaignPlanSelectedDay
                      ) {
                        return true;
                      }
                      return false;
                    }
                  );

                if (!selectedSegmentDateMappings) {
                  const newSegmentStrategyMapping: SegmentDateMappings = {
                    campaignDate: {
                      currentDate: convertISOToLocaleDateString(
                        action.payload.campaignPlanSelectedDay
                      ),
                    },
                    strategy: null,
                    plans: [
                      getFieldsForFirstOutreachFirstDay(action.payload.payload)
                        .plans[0],
                    ],
                  };
                  campaignSegment.segmentDateMappings.push(
                    newSegmentStrategyMapping
                  );
                } else {
                  campaignSegment.segmentDateMappings.forEach(
                    (segmentDateMapping) => {
                      if (
                        segmentDateMapping.campaignDate.currentDate ===
                        action.payload.campaignPlanSelectedDay
                      ) {
                        segmentDateMapping.plans.push(
                          getFieldsForFirstOutreachFirstDay(
                            action.payload.payload
                          ).plans[0]
                        );
                      }
                    }
                  );
                }
              }
            }
          );
        }
      });
    },
    setCampaignNbaStatusFailed: (state) => {
      state.campaignPlanner.campaignNbaStatus = {
        ...(state.campaignPlanner.campaignNbaStatus || {}),
        nbaStatus: NbaCampaignStatus.FAILED,
      };
    },
    updateCampaignNbaTicketRaised: (
      state,
      action: PayloadAction<{ isTicketRaised: boolean }>
    ) => {
      state.campaignPlanner.campaignNbaStatus = {
        ...(state.campaignPlanner.campaignNbaStatus || {}),
        ticketRaised: action.payload.isTicketRaised,
      };
    },
    addConditionOutreachForSegmentAction: (
      state,
      action: PayloadAction<{
        planIndex: number;
        strategyPayload: AddConditionOutreachType;
        segementPayload: { segmentId: number; campaignPlanSelectedDay: string };
        campaignEndDate: string;
      }>
    ) => {
      // 1. check conflict with strategy
      // 2. check outreach is beyond end time
      state.campaignPlanner.campaignSegmentList.forEach((campaignSegment) => {
        if (
          campaignSegment.segment.segmentId ===
          action.payload.segementPayload.segmentId
        ) {
          const shouldAddOutreachesToPlan = checkPlansConflictWithStrategy(
            campaignSegment,
            action.payload.segementPayload.campaignPlanSelectedDay,
            action.payload.strategyPayload.selectedOutreachDayCount,
            action.payload.strategyPayload.conditionOutreaches.conditionOutreach
              .outreach,
            action.payload.campaignEndDate,
            action.payload.strategyPayload.accumulatedTimeSlotsInMinutes
          );

          if (!shouldAddOutreachesToPlan) {
            state.campaignPlanner.campaignSegmentList.forEach(
              (campaignSegment) => {
                if (
                  campaignSegment.segment.segmentId ===
                  action.payload.segementPayload.segmentId
                ) {
                  campaignSegment.segmentDateMappings.forEach(
                    (segmentDateMapping) => {
                      if (
                        segmentDateMapping.campaignDate.currentDate ===
                        action.payload.segementPayload.campaignPlanSelectedDay
                      ) {
                        segmentDateMapping.plans.forEach((plan, index) => {
                          if (index === action.payload.planIndex) {
                            plan.planConditionoutreachList.push(
                              action.payload.strategyPayload.conditionOutreaches
                            );
                          }
                        });
                      }
                    }
                  );
                }
              }
            );
          }
        }
      });
    },
    fetchSegmentsList: (
      state,
      action: PayloadAction<{ allocationId: number | string }>
    ) => {
      state.isLoading = false;
    },
    fetchSegmentsListSuccess: (
      state,
      action: PayloadAction<{
        segmentsList: SegmentsListType[];
        allocationId: number;
      }>
    ) => {
      state.isLoading = false;
    },
    updateStrategyName: (
      state,
      action: PayloadAction<{ strategyName: string }>
    ) => {
      state.createStrategy.strategyName = action.payload.strategyName;
    },
    campaignPlanSaveDraft: (
      state,
      action: PayloadAction<{
        payloadSaveDraft: PayloadSaveDraft;
        campaignId: number;
        campaignPlanSaveDraftSuccessCallback?: () => void;
        showToast: boolean;
      }>
    ) => {
      state.isLoading = true;
    },
    campaignPlanSaveDraftSuccess: (state) => {
      state.isLoading = false;
    },
    updateDateListCampaignPlanner: (
      state,
      action: PayloadAction<{
        dateList: DateList[];
      }>
    ) => {
      state.dateList.forEach((date1) => {
        action.payload.dateList.forEach((updatedDate) => {
          if (updatedDate.date === date1.date) {
            date1.state = updatedDate.state;
          }
        });
      });
    },
    deleteStrategyById: (
      state,
      action: PayloadAction<{
        strategyId: number;
        deleteStrategyByIdSuccessCallback: () => void;
      }>
    ) => {
      state.isLoading = true;
    },
    deleteStrategyByIdSuccess: (
      state,
      action: PayloadAction<{
        strategyId: number;
      }>
    ) => {
      state.strategiesList = state.strategiesList.filter((newStrategy) => {
        if (newStrategy.strategyId === action.payload.strategyId) {
          return false;
        }
        return true;
      });
      state.totalRecords = state.totalRecords - 1;
      state.isLoading = false;
    },
    addStrategyFromLibraryToSegmentInCampaignPlan: (
      state,
      action: PayloadAction<{
        campaignPlanSelectedDay: string;
        segmentId: number;
        strategyFromLibrary: StrategyListType;
      }>
    ) => {
      state.campaignPlanner.campaignSegmentList.forEach((campaignSegment) => {
        if (campaignSegment.segment.segmentId === action.payload.segmentId) {
          campaignSegment.segmentDateMappings =
            campaignSegment.segmentDateMappings.filter((segmentDateMapping) => {
              if (
                segmentDateMapping.campaignDate.currentDate ===
                  action.payload.campaignPlanSelectedDay ||
                segmentDateMapping.blockedByStrategy?.blockedFrom ===
                  action.payload.campaignPlanSelectedDay
              ) {
                return false;
              }
              return true;
            });

          const { isBlocked, messageInfo } =
            checkStrategyConflictWithStrategyAndPlan(
              campaignSegment,
              action.payload.strategyFromLibrary,
              action.payload.campaignPlanSelectedDay
            );

          if (isBlocked) {
            message.open({
              content: messageInfo,
              type: 'error',
            });
            return;
          }

          if (!isBlocked) {
            const newStrategy = {
              strategyId: action.payload.strategyFromLibrary.strategyId,
              strategyName: action.payload.strategyFromLibrary.strategyName,
              daysCount: action.payload.strategyFromLibrary.daysCount,
              totalNoOfOutreaches:
                action.payload.strategyFromLibrary.totalNoOfOutreaches,
            };
            const newSegmentStrategyMapping: SegmentDateMappings = {
              campaignDate: {
                campaignDateId: 1,
                currentDate: action.payload.campaignPlanSelectedDay,
              },
              strategy: newStrategy,
              plans: [],
            };
            campaignSegment.segmentDateMappings.push(newSegmentStrategyMapping);
            for (
              let i = 1;
              i < action.payload.strategyFromLibrary.daysCount;
              i++
            ) {
              const strategyAttachDate = increaseDateByOneDay(
                action.payload.campaignPlanSelectedDay,
                i
              );
              campaignSegment.segmentDateMappings =
                campaignSegment.segmentDateMappings.filter(
                  (segmentDateMapping) => {
                    return !(
                      segmentDateMapping.campaignDate.currentDate ===
                      strategyAttachDate
                    );
                  }
                );

              const newSegmentStrategyMapping2: SegmentDateMappings = {
                campaignDate: {
                  campaignDateId: 1,
                  currentDate: strategyAttachDate,
                },
                strategy: newStrategy,
                plans: [],
                isBlockedByStrategy: true,
                blockedByStrategy: {
                  strategyId: action.payload.strategyFromLibrary.strategyId,
                  strategyName: action.payload.strategyFromLibrary.strategyName,
                  strategyDaysCount:
                    action.payload.strategyFromLibrary.daysCount,
                  blockedFrom: action.payload.campaignPlanSelectedDay,
                },
              };

              campaignSegment.segmentDateMappings.push(
                newSegmentStrategyMapping2
              );
            }
          }
        }
      });
    },

    createCampaignWithAllocation: (
      state,
      action: PayloadAction<{
        allocationId: number;
        allocationName: string;
        createCampaignWithAllocationCallback: (campaignId: number) => void;
      }>
    ) => {
      state.isLoading = true;
    },
    createCampaignWithAllocationSuccess: (
      state,
      action: PayloadAction<CreateCampaignWithAllocationResponseData>
    ) => {
      state.isLoading = false;
      state.campaignPlanner.allocationId = action.payload.allocationId;
      state.campaignPlanner.allocationName = action.payload.allocationName;
      state.campaignPlanner.campaignId = action.payload.campaignId;
    },
    pauseOrResumeCampaign: (
      state,
      action: PayloadAction<{
        campaignId: number;
        status: string;
        pauseOrResumeCampaignSuccessCallback?: () => void;
      }>
    ) => {
      state.isLoading = true;
    },
    pauseOrResumeCampaignSucceeded: (
      state,
      action: PayloadAction<ICampaignData>
    ) => {
      state.campaignPlanner.paused = action.payload.paused;
      state.isLoading = false;
    },
    pauseOrResumeCampaignFailed: (
      state,
      action: PayloadAction<IActionPayload>
    ) => {
      state.isLoading = false;
    },
    updatesDaysInStrategy: (state) => {
      state.createStrategy.days = [];
    },
    removeStrategyFromLibraryFromSegmentInCampaignPlan: (
      state,
      action: PayloadAction<{
        campaignPlanSelectedDay: string;
        segmentId: number;
        strategyDaysCount: number;
      }>
    ) => {
      state.campaignPlanner.campaignSegmentList.forEach((campaignSegment) => {
        if (campaignSegment.segment.segmentId === action.payload.segmentId) {
          campaignSegment.segmentDateMappings =
            campaignSegment.segmentDateMappings.filter((segmentDateMapping) => {
              if (
                segmentDateMapping.campaignDate.currentDate ===
                  action.payload.campaignPlanSelectedDay ||
                segmentDateMapping.blockedByStrategy?.blockedFrom ===
                  action.payload.campaignPlanSelectedDay
              ) {
                return false;
              }
              return true;
            });
        }
      });
    },
    updateActiveAccordionAccordions: (
      state,
      action: PayloadAction<{
        activeAccordions: string[];
      }>
    ) => {
      state.activeAccordions = action.payload.activeAccordions;
    },
    resetStrategyAction: (state) => {
      state.createStrategy = {
        strategyName: '',
        daysCount: 0,
        intensity: '',
        isMapped: false,
        isLibrary: true,
        createdDate: '',
      };

      state.selectedPlanDay = 1;
      state.isStrategyCreated = false;
    },
    deleteParentOutreachNode: (
      state,
      action: PayloadAction<{
        planIndex: number;
        selectedPlanDay: number;
      }>
    ) => {
      state.createStrategy.days?.forEach((value) => {
        if (value.currentDay === action.payload.selectedPlanDay) {
          value.plans = value.plans.filter((plan, index) => {
            return index !== action.payload.planIndex;
          });
        }
      });
    },
    fetchOutreachConfig: (
      state,
      action: PayloadAction<{ lenderId: string }>
    ) => {
      state.isLoading = true;
    },
    fetchOutreachConfigSuccess: (
      state,
      action: PayloadAction<OutreachConfig>
    ) => {
      state.isLoading = false;
      state.outreachConfig.channel = action.payload.channel;
      state.outreachConfig.languages = action.payload.languages;
    },
    cloneStrategyById: (
      state,
      action: PayloadAction<{
        strategyId: number;
        limit: number;
        cloneStrategyByIdSuccessCallback: (clonedStrategyId: number) => void;
      }>
    ) => {
      state.isLoading = true;
    },
    cloneStrategyByIdSuccess: (
      state,
      action: PayloadAction<{ strategy: Data; limit: number }>
    ) => {
      state.isLoading = false;
      const strategyTableData: StrategyListType = {
        strategyName: action.payload.strategy.strategyName,
        daysCount: action.payload.strategy.daysCount,
        isMapped: false,
        createdDate: action.payload.strategy.createdDate || '',
        lastModified: '',
        lastUsed: '',
        strategyId: action.payload.strategy.strategyId || 0,
        createdBy: action.payload.strategy.createdBy || '',
        lastModifiedBy: action.payload.strategy.lastModifiedDate || '',
        totalNoOfOutreaches: action.payload.strategy.totalNoOfOutreaches || 0,
      };
      state.strategiesList.unshift(strategyTableData);
      if (state.strategiesList.length > action.payload.limit) {
        state.strategiesList.pop();
      }
      state.totalRecords = state.totalRecords + 1;
    },
    getContentConfig: (
      state,
      action: PayloadAction<{
        language: string;
        lenderId: string;
      }>
    ) => {
      state.isLoading = true;
    },
    getContentConfigSuccess: (
      state,
      action: PayloadAction<{ contents: Content[] }>
    ) => {
      state.isLoading = false;
      state.outreachConfig.contents = action.payload.contents;
    },
    deleteConditionOutreachesInCampaignPlan: (
      state,
      action: PayloadAction<{
        strategyPayload: DeleteConditionType;
        segmentPayload: SegmentPayload;
      }>
    ) => {
      state.isLoading = true;
      state.campaignPlanner.campaignSegmentList.forEach((campaignSegment) => {
        if (
          campaignSegment.segment.segmentId ===
          action.payload.segmentPayload.segmentId
        ) {
          campaignSegment.segmentDateMappings.forEach((segmentDateMapping) => {
            if (
              segmentDateMapping.campaignDate.currentDate ===
              action.payload.segmentPayload.campaignPlanSelectedDay
            ) {
              segmentDateMapping.plans.forEach((plan, index) => {
                if (index === action.payload.segmentPayload.planIndex) {
                  plan.planConditionoutreachList =
                    plan.planConditionoutreachList.filter((condition) => {
                      if (
                        action.payload.strategyPayload.deleteConditions.includes(
                          condition.conditionOutreach.currentNode
                        )
                      ) {
                        return false;
                      }
                      return true;
                    });
                }
              });
            }
          });
        }
      });
      state.isLoading = false;
    },
    addOutreachFromConditionCampaignPlan: (
      state,
      action: PayloadAction<{
        outreaches: AddOutreachType;
        campaignPlanSelectedDay: string;
        index: number;
        segmentId: number;
      }>
    ) => {
      state.campaignPlanner.campaignSegmentList.forEach((campaignSegment) => {
        if (campaignSegment.segment.segmentId === action.payload.segmentId) {
          campaignSegment.segmentDateMappings.forEach((segmentDateMapping) => {
            if (
              segmentDateMapping.campaignDate.currentDate ===
              action.payload.campaignPlanSelectedDay
            ) {
              segmentDateMapping.plans.forEach((plan, index) => {
                if (index === action.payload.index) {
                  plan.planConditionoutreachList.forEach((condition) => {
                    if (
                      condition.conditionOutreach.currentNode ===
                      action.payload.outreaches.currentNode
                    ) {
                      condition.conditionOutreach.outreach.push(
                        ...action.payload.outreaches.selectedOutreaches
                      );
                    }
                  });
                }
              });
            }
          });
        }
      });
    },
    deleteFromOutreachCampaignPlan: (
      state,
      action: PayloadAction<{
        conditionPayload: DeleteConditionType;
        segmentId: number;
        campaignPlanSelectedDay: string;
        planIndex: number;
      }>
    ) => {
      state.campaignPlanner.campaignSegmentList.forEach((campaignSegment) => {
        if (campaignSegment.segment.segmentId === action.payload.segmentId) {
          campaignSegment.segmentDateMappings.forEach((segmentDateMapping) => {
            if (
              segmentDateMapping.campaignDate.currentDate ===
              action.payload.campaignPlanSelectedDay
            ) {
              segmentDateMapping.plans.forEach((plan, index) => {
                if (index === action.payload.planIndex) {
                  plan.planConditionoutreachList =
                    plan.planConditionoutreachList.filter((condition) => {
                      if (
                        action.payload.conditionPayload.deleteConditions.includes(
                          condition.conditionOutreach.currentNode
                        )
                      ) {
                        return false;
                      }
                      return true;
                    });
                }
              });
            }
          });
        }
      });
    },
    deleteCurrentOutreachCampaignPlan: (
      state,
      action: PayloadAction<{
        outReachPayload: DeleteOutreachType;
        planIndex: number;
        segmentId: number;
        campaignPlanSelectedDay: string;
      }>
    ) => {
      state.campaignPlanner.campaignSegmentList.forEach((campaignSegment) => {
        if (campaignSegment.segment.segmentId === action.payload.segmentId) {
          campaignSegment.segmentDateMappings.forEach((segmentDateMapping) => {
            if (
              segmentDateMapping.campaignDate.currentDate ===
              action.payload.campaignPlanSelectedDay
            ) {
              segmentDateMapping.plans.forEach((plan, index) => {
                if (index === action.payload.planIndex) {
                  plan.planConditionoutreachList.forEach((condition) => {
                    if (
                      condition.conditionOutreach.currentNode ===
                      action.payload.outReachPayload.nodeId
                    ) {
                      condition.conditionOutreach.outreach =
                        condition.conditionOutreach.outreach.filter(
                          (outreach) => {
                            if (
                              outreach.outreachId ===
                              action.payload.outReachPayload.outreachId
                            ) {
                              return false;
                            }
                            return true;
                          }
                        );
                    }
                  });
                }
              });
            }
          });
        }
      });
    },
    deleteParentOutreachCampaignPlan: (
      state,
      action: PayloadAction<{
        planIndex: number;
        segmentId: number;
        campaignPlanSelectedDay: string;
      }>
    ) => {
      state.campaignPlanner.campaignSegmentList.forEach((campaignSegment) => {
        if (campaignSegment.segment.segmentId === action.payload.segmentId) {
          campaignSegment.segmentDateMappings.forEach((segmentDateMapping) => {
            if (
              segmentDateMapping.campaignDate.currentDate ===
              action.payload.campaignPlanSelectedDay
            ) {
              segmentDateMapping.plans = segmentDateMapping.plans.filter(
                (_, index) => {
                  return index !== action.payload.planIndex;
                }
              );
            }
          });
        }
      });
    },
    planCampaignPlan: (
      state,
      action: PayloadAction<{
        campaignId: number;
        lenderId: string;
        planCampaignPlanCallback: () => void;
      }>
    ) => {
      state.isLoading = true;
    },
    updateEnableStrategyLibraryInCampaignPlan: (
      state,
      action: PayloadAction<{
        segmentId: number;
        enableStrategyLibrary: boolean;
      }>
    ) => {
      const activeStrategySwitchObj = state.activeStrategySwitch.find(
        (item) => item.segmentId === action.payload.segmentId
      );

      if (activeStrategySwitchObj) {
        state.activeStrategySwitch = state.activeStrategySwitch.map((item) => {
          if (item.segmentId === action.payload.segmentId) {
            return {
              ...item,
              enableStrategyLibrary: action.payload.enableStrategyLibrary,
            };
          }
          return item;
        });

        return;
      }

      state.activeStrategySwitch.push({
        segmentId: action.payload.segmentId,
        enableStrategyLibrary: action.payload.enableStrategyLibrary,
      });
    },
    resetCampaignPlan: () => {
      return strategyInitialState;
    },

    setEditStatus: (
      state,
      action: PayloadAction<{
        isEdited: boolean;
      }>
    ) => {
      state.campaignPlanner.edited = action.payload.isEdited;
    },
    setSelectedChannel: (
      state,
      action: PayloadAction<{
        selectedChannel: string;
      }>
    ) => {
      state.selectedChannel = action.payload.selectedChannel;
    },
    createCampaign: (state, action: CreateCampaignActionType) => {
      state.isLoading = true;
    },
    createCampaignSucceeded: (state, action: PayloadAction<ICampaignData>) => {
      state.isLoading = false;
      state.campaignPlanner.allocationId = action.payload.allocationId || '';
      state.campaignPlanner.allocationName = action.payload.allocationName;
      state.campaignPlanner.currentStatus = action.payload.currentStatus;
      state.campaignPlanner.startDate = action.payload.startDate;
      state.campaignPlanner.endDate = action.payload.endDate;
      state.campaignPlanner.campaignId = action.payload.campaignId;
      state.campaignPlanner.campaignName = action.payload.campaignName;
      state.campaignPlanner.lob = action.payload.lob;
      state.campaignPlanner.records = action.payload.records;
    },
    createCampaignFailed: (state) => {
      state.isLoading = false;
    },
    updateCampaign: (state, action: UpdateCampaignActionType) => {
      state.isLoading = true;
    },
    setCampaignPlanningLoading: (
      state,
      action: PayloadAction<{ isCampaignPlanningLoading: boolean }>
    ) => {
      state.isCampaignPlanningLoading =
        action.payload.isCampaignPlanningLoading;
    },
  },
});

export const {
  createCampaign,
  createCampaignSucceeded,
  createCampaignFailed,
  updateCampaign,
  addNewOutreach,
  addConditionOutreachAction,
  selectPlanDay,
  selectPlanDays,
  selectPlanIntensity,
  createFirstOutreachPlan,
  fetchStrategyById,
  fetchStrategyByIdSuccess,
  fetchConditionsDropdownList,
  fetchConditionsDropdownListSuccess,
  fetchOutreachDropdownList,
  fetchOutreachDropdownListSuccess,
  deleteConditionOutreaches,
  deleteOutreach,
  fetchStrategies,
  fetchStrategiesSuccess,
  resetStrategiesList,
  selectCampaignPlanDate,
  createParentOutreachPlanSegment,
  fetchCampaignPlan,
  fetchCampaignPlanSuccess,
  addConditionOutreachForSegmentAction,
  fetchSegmentsList,
  fetchSegmentsListSuccess,
  updateStrategyName,
  campaignPlanSaveDraft,
  campaignPlanSaveDraftSuccess,
  updateDateListCampaignPlanner,
  deleteStrategyById,
  deleteStrategyByIdSuccess,
  addStrategyFromLibraryToSegmentInCampaignPlan,
  createCampaignWithAllocation,
  createCampaignWithAllocationSuccess,
  pauseOrResumeCampaign,
  pauseOrResumeCampaignSucceeded,
  pauseOrResumeCampaignFailed,
  updatesDaysInStrategy,
  updateActiveAccordionAccordions,
  removeStrategyFromLibraryFromSegmentInCampaignPlan,
  resetStrategyAction,
  fetchStrategiesFail,
  deleteParentOutreachNode,
  fetchOutreachConfig,
  fetchOutreachConfigSuccess,
  cloneStrategyById,
  cloneStrategyByIdSuccess,
  getContentConfig,
  getContentConfigSuccess,
  deleteConditionOutreachesInCampaignPlan,
  addOutreachFromConditionCampaignPlan,
  deleteFromOutreachCampaignPlan,
  deleteCurrentOutreachCampaignPlan,
  deleteParentOutreachCampaignPlan,
  planCampaignPlan,
  updateEnableStrategyLibraryInCampaignPlan,
  resetCampaignPlan,
  setEditStatus,
  setSelectedChannel,
  setCampaignNbaStatusFailed,
  updateCampaignNbaTicketRaised,
  setLoadingStrategyState,
  setCampaignPlanningLoading,
} = strategy.actions;
export default strategy.reducer;
